// Oracle procedural feed (ProceduralBodyIndexing Slice 4.1).
//
// Enumerates standalone stored procedures (`ALL_PROCEDURES` filtered by
// `OBJECT_TYPE = 'PROCEDURE'` — package-internal procedures live under
// `OBJECT_TYPE = 'PACKAGE'` with `PROCEDURE_NAME` set, and are an explicit
// out-of-scope v1.1 follow-up per PRD §4) + triggers (`ALL_TRIGGERS`).
// Procedure bodies come back from `ALL_SOURCE` (one row per source line —
// concatenated by LINE) and ship as `PROCEDURE name(...) IS ... BEGIN ...
// END;`. Trigger bodies come from `ALL_TRIGGERS.TRIGGER_BODY` as the literal
// inner block (no `CREATE TRIGGER ...` wrapper).
//
// Both shapes are wrapped in (possibly nested) `BEGIN ... END;` blocks. The
// universal scanner with `engine: 'oracle'` splits on `;` but treats `BEGIN`
// as a transaction-control keyword, so the chunk after the outer BEGIN
// starts with `BEGIN ...` and gets classified as `transaction-control` (not
// `select`). To surface the inner SELECT chunks, `stripOracleProceduralWrapper`
// replaces every leading `PROCEDURE ... IS` / `BEGIN` / nested `BEGIN` plus
// trailing matching `END;` keywords with spaces (preserves byte offsets so
// `range.startOffset` still aligns with the raw definition). Mirror of
// `pgsqlProceduralFetcher`'s `stripPgProceduralWrapper` step 2 / `mysql`'s
// `stripMysqlProceduralWrapper` — same shape, applied repeatedly to handle
// nested blocks. System owners (`SYS`, `SYSTEM`, `XDB`, …) are skipped at
// the catalog query layer (mirror of MSSQL's `is_ms_shipped = 0` posture).

import type {
  ObjectKind,
  PersistedObjectListing,
  QualifiedName,
} from '../../types';

import {
  asString,
  escapeSqlLiteral,
  fetchListingRows,
  fetchRows,
  fetchSourceRowsPaged,
  makeListing,
  type NavListingFetchOptions,
} from './queryHelper';

const SYSTEM_OWNERS = `('SYS', 'SYSTEM', 'XDB', 'CTXSYS', 'MDSYS', 'OUTLN',
  'DBSNMP', 'WMSYS', 'APPQOSSYS', 'AUDSYS', 'GSMADMIN_INTERNAL', 'OJVMSYS',
  'ORDDATA', 'ORDSYS', 'OLAPSYS', 'EXFSYS', 'DVSYS', 'LBACSYS', 'DBSFWUSER',
  'GGSYS', 'PUBLIC')`;

// Use `ALL_OBJECTS` for the catalog enumeration — `ALL_PROCEDURES` exists
// but its `PROCEDURE_NAME IS NULL` filter for standalone procedures can be
// flaky across Oracle versions (some report `PROCEDURE_NAME = OBJECT_NAME`).
// `ALL_OBJECTS WHERE OBJECT_TYPE = 'PROCEDURE'` exclusively enumerates
// standalone procedures (package-internal members appear under
// `OBJECT_TYPE = 'PACKAGE'` / `PACKAGE BODY`, never as 'PROCEDURE').
const PROC_LIST_SQL = `SELECT OWNER AS "schema", OBJECT_NAME AS "name"
FROM ALL_OBJECTS
WHERE OBJECT_TYPE = 'PROCEDURE'
  AND OWNER NOT IN ${SYSTEM_OWNERS}`;

// No inline ORDER BY: the paged fetch appends `ORDER BY OWNER, NAME, LINE`
// (its stable page key) followed by the row-limiting clause. Lines are
// concatenated AFTER every page has landed, so a procedure body straddling a
// page boundary still reassembles in line order.
const PROC_SOURCE_SQL = `SELECT OWNER AS "schema", NAME AS "name", LINE AS "line", TEXT AS "text"
FROM ALL_SOURCE WHERE TYPE = 'PROCEDURE' AND OWNER NOT IN ${SYSTEM_OWNERS}`;

const TRIGGER_SQL = `SELECT OWNER AS "schema", TRIGGER_NAME AS "name", TRIGGER_BODY AS "body"
FROM ALL_TRIGGERS WHERE OWNER NOT IN ${SYSTEM_OWNERS}`;

function listing(
  objectKind: PersistedObjectListing['objectKind'],
  schema: string,
  name: string,
  bodyText: string,
): PersistedObjectListing {
  return makeListing('oracle', objectKind, schema, name, bodyText);
}

/** Replace every `BEGIN`/`END[;]` keyword pair (outermost first, then nested
 *  inner blocks) with spaces, plus the leading `PROCEDURE … IS|AS` /
 *  `FUNCTION … IS|AS` header. Preserves byte offsets so emitted ranges still
 *  align with the raw definition. The remaining text contains the inner
 *  statements which the universal scanner will split on `;` and classify as
 *  `kind === 'select'`. Function-call ENDs (`END IF`, `END LOOP`, `END CASE`)
 *  are deliberately left untouched — they're not block terminators, just
 *  control-structure closers. */
export function stripOracleProceduralWrapper(body: string): string {
  const chars = body.split('');
  // Strip the leading `(PROCEDURE|FUNCTION) name(...) ... (IS|AS)` header.
  // Anchored at start-of-body so trigger bodies (which begin with `BEGIN`,
  // not `PROCEDURE`/`FUNCTION`) skip this step entirely.
  const headerRe = /^\s*(?:PROCEDURE|FUNCTION)\b[\s\S]*?\b(?:IS|AS)\b/i;
  const header = headerRe.exec(body);
  if (header) {
    for (let i = 0; i < header.index + header[0].length; i++) {
      if (chars[i] !== '\n') chars[i] = ' ';
    }
  }
  // Strip every `BEGIN` keyword (PL/SQL uses `BEGIN` only for block opening
  // so swallowing every occurrence is safe — there are no transaction-control
  // BEGINs in routine bodies).
  let s = chars.join('');
  s = s.replace(/\bBEGIN\b/gi, (m) => ' '.repeat(m.length));
  // Strip every block-terminating `END;` (with optional `<label>` between END
  // and `;`). Control-structure terminators (`END IF;`, `END LOOP;`,
  // `END CASE;`) are excluded via the negative lookahead so they survive —
  // they're valid PL/SQL inside the inner statements. Requires `;` at the
  // end so we don't accidentally span a newline into a following block END.
  s = s.replace(
    /\bEND\b(?!\s+(?:IF|LOOP|CASE)\b)(?:\s+\w+)?\s*;/gi,
    (m) => ' '.repeat(m.length),
  );
  return s;
}

export async function listOracleProceduralObjects(
  options: NavListingFetchOptions,
): Promise<readonly PersistedObjectListing[]> {
  const [procListRows, sourceRows, triggerRows] = await Promise.all([
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-procedures',
      sql: PROC_LIST_SQL,
      orderBy: 'OWNER, OBJECT_NAME',
      dialect: 'offset-fetch',
      // The catalog feed carries no body text — only the source feed does.
      bodyColumns: [],
    }),
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-procedure-source',
      sql: PROC_SOURCE_SQL,
      orderBy: 'OWNER, NAME, LINE',
      dialect: 'offset-fetch',
      bodyColumns: ['text'],
    }),
    fetchListingRows(options, {
      engine: 'oracle',
      label: 'oracle-triggers',
      sql: TRIGGER_SQL,
      orderBy: 'OWNER, TRIGGER_NAME',
      dialect: 'offset-fetch',
      bodyColumns: ['body'],
    }),
  ]);

  const out: PersistedObjectListing[] = [];

  // Concatenate ALL_SOURCE rows by (schema, name) — Oracle ships routine
  // bodies as one row per source line.
  const bodies = new Map<string, { schema: string; name: string; body: string }>();
  for (const row of sourceRows) {
    const schema = asString(row.schema);
    const name = asString(row.name);
    if (!name) continue;
    const key = `${schema}.${name}`;
    const entry = bodies.get(key) ?? { schema, name, body: '' };
    entry.body += asString(row.text);
    bodies.set(key, entry);
  }

  for (const row of procListRows) {
    const schema = asString(row.schema);
    const name = asString(row.name);
    if (!name) continue;
    const entry = bodies.get(`${schema}.${name}`);
    if (!entry || !entry.body.trim()) continue;
    out.push(listing('procedure', schema, name, stripOracleProceduralWrapper(entry.body)));
  }

  for (const row of triggerRows) {
    const name = asString(row.name);
    const body = asString(row.body);
    if (!name || !body) continue;
    out.push(listing('trigger', asString(row.schema), name, stripOracleProceduralWrapper(body)));
  }

  return out;
}

export async function fetchOracleProceduralObjectBody(
  connectionId: string,
  database: string,
  objectKind: ObjectKind,
  objectName: QualifiedName,
): Promise<string | null> {
  // Owner as given — see `fetchOracleObjectBody`.
  const s = escapeSqlLiteral(objectName.schema ?? '');
  const n = escapeSqlLiteral(objectName.name);

  if (objectKind === 'procedure') {
    // One row per source line — paged, so a long procedure comes back whole
    // (a fixed row cap used to cut it at 1,000 lines and index the stub).
    const sql = `SELECT TEXT AS "text" FROM ALL_SOURCE
      WHERE OWNER = '${s}' AND NAME = '${n}' AND TYPE = 'PROCEDURE'`;
    const rows = await fetchSourceRowsPaged(connectionId, database, 'oracle', 'oracle-procedure-body', sql, 'LINE');
    if (rows.length === 0) return null;
    const body = rows.map((r) => asString(r.text)).join('');
    return body.trim() ? stripOracleProceduralWrapper(body) : null;
  }

  if (objectKind === 'trigger') {
    const sql = `SELECT TRIGGER_BODY AS "body" FROM ALL_TRIGGERS
      WHERE OWNER = '${s}' AND TRIGGER_NAME = '${n}'`;
    const rows = await fetchRows(connectionId, database, sql, 'oracle-trigger-body');
    const raw = asString(rows[0]?.body);
    return raw ? stripOracleProceduralWrapper(raw) : null;
  }

  return null;
}
